<template>
  <slot></slot>
  <modal :id="modalId" :title="title" @shown="modalShown">

    <div v-if="!immutableMode" class="text-center alert alert-info">
      <p>This is a preview of merged application. Are you sure you want to proceed?</p>
    </div>

    <!-- Errors list -->
    <div v-if="Object.keys(errors).length" class="alert alert-warning text-danger" role="alert">
      <ul id="errors_list" class="m-b-xs">
        <li v-if="errors['_']">
          {{ errors['_'] }}
        </li>
        <li v-show="key !== '_'" v-for="(error, key) in errors">
          {{ error }}
        </li>
      </ul>
    </div>

    <div class="row">

      <!-- Application text fields (with photo) preview -->

      <div class="col-md-6">
        <table class="table">
          <tbody>
          <tr v-for="(field, fieldKey) in fields">
            <td class="font-bold text-right left-header"><b>{{ field['name'] }}</b></td>
            <td v-if="field['type'] !== 'image'">
              <div>
                <span>{{ application[fieldKey] }}</span>
              </div>
            </td>
            <td v-else>
              <img :src="application[fieldKey]" class="img-thumbnail img-lg">
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="col-md-6">

        <!-- Application tables preview (field experience, desk experience, certifications, licenses) -->

        <div v-for="(table, tableKey) in tables" class="mb-3">
          <div>
            <h4>{{ table.name }}</h4>
            <table class="table table-bordered table-hover mb-0">
              <thead>
              <tr>
                <th v-for="header in table.header">{{ header }}</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in application[tableKey]">
                <td v-show="!k.startsWith('_') && k !== 'id'" v-for="(value, k) in item">{{ value }}</td>
              </tr>
              <tr v-if="!application[tableKey] || application[tableKey].length === 0">
                <td :colspan="table.header.length">{{ table['no-data-message'] }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Application attachments preview -->

        <h4>Attachments</h4>
        <template v-if="!attachmentsListIsEmpty">
        <div v-for="(attachmentObject, type) in attachments">
          <h5>{{ type }}</h5>
          <ul>
            <li v-for="attachment in attachmentObject">
              <a :href="attachment['link']" target="_blank">{{ attachment['name'] }}</a>
              <i v-if="!attachment['valid']" class="fa fa-exclamation-triangle text-danger"
                 data-toggle="tooltip" data-placement="top" data-boundary="window"
                 :id="'merged' + attachment['id']" :data-container="'#merged' + attachment['id']"
                 title="This document appears to be corrupt or empty"></i>
              <br/>
              <small>Size: {{ bytesFormatted(attachment['size']) }}</small>
              <small class="text-danger" v-if="attachment['error']">{{ attachment['error'] }}</small>
            </li>
          </ul>
        </div>
        </template>
        <div v-else>
          No attachments
        </div>

      </div>
    </div>

    <template #footer>
      <template v-if="immutableMode">
        <button type="button" class="btn btn-default" @click="hideModal">
          <i class="fa fa-fw fa-ban"></i> Close
        </button>
      </template>
      <template v-else>
        <div v-if="application.approved" class="alert alert-warning m-t-xs m-t-sm">
          <strong>"Merge & Re-approve" Action Warning</strong><br>
          This application has already been approved before and is associated with an adjuster profile.
          "Re-approve" will update the adjuster profile, any existing data will be overwritten with the application data.
        </div>

        <button type="button" class="btn btn-default" @click="hideModal">
          <i class="fa fa-fw fa-ban"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" @click.prevent="submit(false)">
          <i class="fa fa-fw fa-save"></i> Merge
        </button>
        <button v-if="application.approved" type="button" class="btn btn-primary" @click.prevent="submit(true)">
          <i class="fa fa-fw fa-save"></i> Merge & Re-approve
        </button>
      </template>
    </template>

    <slot name="hidden"></slot>

  </modal>
</template>

<style scoped>
#errors_list li {
  list-style-type: none;
}
</style>

<script>

import $ from "jquery";
import HasModal from "../mixins/HasModal";

/**
 *
 * Component used to display quick preview of application on merge target application selection page
 * and to preview merge result on merging page.
 *
 * Input properties:
 *
 * modalId - id attribute of modal dialog.
 * title - modal dialog title.
 * action - action to submit merging result (not used in immutable mode).
 * fields - description on application model fields.
 * tables - description on application model tables (field experience, desk experience, licenses, certifications).
 * duplicateIds - list of duplicates ids.
 * immutableMode - if true, submit ability is absent.
 *
 *
 * Emits:
 *
 * error - error event with validation errors.
 *
 *
 * Usage examples:
 *
 * <application-merge-preview-modal title="Preview of Application"
 *                                  modal-id="application-preview-modal"
 *                                  :application="{{ json_encode($application) }}"
 *                                  :attachments="{{ json_encode($attachments) }}"
 *                                  :tables="{{ json_encode($tables_description) }}"
 *                                  :fields="{{ json_encode($fields_description) }}"
 *                                  :immutable-mode="true">
 *  <a href="#" v-modal="'application-preview-modal'">Preview</a>
 * </application-merge-preview-modal>
 */

export default {
  mixins: [HasModal],

  emits: ['success', 'error'],

  props: {

    application: {
      type: Object,
      required: false
    },
    attachments: {
      type: Object,
      default: () => {return {}}
    },
    overrideFields: {
      type: Object,
      default: () => {return {}}
    },
    selectedFields: {
      type: Object,
      default: () => {return {}}
    },
    selectedTables: {
      type: Object,
      default: () => {return {}}
    },
    selectedAttachments: {
      type: Object,
      default: () => {return {}}
    },
    modalId: {
      type: String,
      required: false,
      default: 'application-merge-modal'
    },
    title: {
      type: String,
      required: false,
      default: "Merged application preview"
    },
    action: {
      type: String,
      default: ''
    },
    fields: {
      type: Object,
      required: true,
    },
    tables: {
      type: Object,
      required: true,
    },
    duplicateIds: {
      type: Array,
      default: () => { return []; },
    },
    immutableMode: {
      type: Boolean,
      default: false,
    }
  },

  data() {

    return {
      errors: {}
    };

  },
  computed: {

    method() {
      return "POST";
    },

    // Determine if attachments list is empty.
    attachmentsListIsEmpty() {
      return this.attachments ? Object.keys(this.attachments).length === 0 : true;
    },

  },
  methods: {
    handleError(error) {

      this.errors = {};

      if (error.response.status === 422) {

        // Collecting errors with field names.
        Object.entries(error.response.data.errors).forEach(entry => {
          let fieldName = entry[0];
          this.errors[fieldName] = entry[1].join(' ');
        });

      } else {

        let errorMessage = 'Failed to merge Application.';

        if (error.response) {
          // Request made and server responded
          if (error.response.data.message === 'CSRF token mismatch.') {
            errorMessage += ' The form is outdated. Please reload this page and try again.';
          }
        }

        this.errors['_'] = errorMessage;
      }
    },

    submit(reApprove = false) {

      if (this.immutableMode) return;

      let _this = this;

      let selectedTableItems = {};
      Object.keys(this.tables).forEach(tableKey => {
        selectedTableItems[tableKey] = this.application[tableKey].map(item => item.id);
      });

      let selectedAttachmentItems = [];
      Object.keys(this.attachments).forEach(fileType => {
        this.attachments[fileType].forEach(item => {
          selectedAttachmentItems.push(item.id);
        });
      });

      let data = {
        'override-fields': this.overrideFields,
        'selected-tables': this.selectedTables,
        'selected-fields': this.selectedFields,
        'selected-attachments': this.selectedAttachments,
        'selected-table-items': selectedTableItems,
        'selected-attachment-items': selectedAttachmentItems,
        'application_ids': this.duplicateIds,
        '_token': this.$csrf_token,
        '_method': this.method,
      }

      if (reApprove) data.re_approve = true;

      axios.post(this.action, data)
          .then(response => {
            _this.$emit('success');
            _this.hideModal();
          })
          .catch(error => {
            _this.handleError(error);
            _this.$emit('error', _this.errors);
          });
    },

    // Format bytes output.
    bytesFormatted(bytes) {
      bytes = parseInt(bytes);
      let sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      if (bytes === 0) return '0 Byte';
      let i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
    },

    modalShown() {
      $('[data-toggle="tooltip"]').tooltip();
    },

  }
}

</script>
